<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { Folder, Shield, SquareLibrary, Package, GraduationCap } from "@lucide/vue";
import SidebarNavItem from "./SidebarNavItem.vue";
import ProfileMenu from "./ProfileMenu.vue";
import Divider from "@/components/Reusables/Divider/Divider.vue";
import { useAuthStateStore } from "../../stores/Utils/auth/AuthState";
import { useTenantFeatureAccess } from "@/composables/useTenantFeatureAccess";
import { replayWelcomeDeck } from "@/features/WelcomeFlow";
import { CONSTRUCTION_FORM_ROUTE_NAMES } from "@/router/constructionRoutes";
import { ADMIN_NAV_ACTIVE_ROUTE_NAMES, PROJECT_NAV_ACTIVE_ROUTE_NAMES } from "@/router/navigationLayout";

defineProps<{
  isExpanded: boolean;
  lCAdaIsShown: boolean;
}>();

const emit = defineEmits<{
  toggleLCAda: [shown: boolean];
}>();

// The rows stay active across their child routes (a project page keeps Projects lit), but each guide
// belongs to ONE page, so the ⓘ is gated on that route rather than on the row being active.
const route = useRoute();

const authStore = useAuthStateStore();
const { isPlatformAdmin } = storeToRefs(authStore);

// The AI Agent/Skills surface is gated on the same Frontegg entitlement as the AI Agent chat panel.
const { canUseAiChat } = useTenantFeatureAccess();

// Every page's guide replays from its own nav row's ⓘ, on demand, any number of times — replayWelcomeDeck
// closes whatever flow guide might already be running and logs the replay.
const openProjectsGuide = () => replayWelcomeDeck("platform");

const openLibraryGuide = () => replayWelcomeDeck("library");
</script>

<template>
  <div class="flex h-full flex-col p-2">
    <div class="gap-px-4 flex flex-col">
      <SidebarNavItem
        label="Projects"
        to="/"
        :icon-component="Folder"
        :active-names="[...PROJECT_NAV_ACTIVE_ROUTE_NAMES, 'projectLcaPdfReportPage']"
        link-id="projects-nav-link"
        padding-class="px-2.5 py-1.5"
        :guide="route.name === 'projects'"
        guide-always-visible
        :is-expanded="isExpanded"
        @open-guide="openProjectsGuide" />
      <SidebarNavItem
        label="Library"
        to="/library"
        :icon-component="SquareLibrary"
        :active-names="[
          'materialsDatabase',
          'createMaterial',
          'libraryDetail',
          'libraryCompare',
          ...CONSTRUCTION_FORM_ROUTE_NAMES,
        ]"
        link-id="library-nav-link"
        padding-class="px-2.5 py-1.5"
        :guide="route.name === 'materialsDatabase'"
        guide-always-visible
        :is-expanded="isExpanded"
        @open-guide="openLibraryGuide" />
      <SidebarNavItem
        v-if="isPlatformAdmin"
        label="Admin Panel"
        to="/admin"
        :icon-component="Shield"
        :active-names="[...ADMIN_NAV_ACTIVE_ROUTE_NAMES]"
        link-id="sidebar-admin-panel-nav-link"
        padding-class="px-2.5 py-1.5"
        :is-expanded="isExpanded" />

      <SidebarNavItem
        v-if="canUseAiChat"
        label="AI Agent"
        to="/agent/skills"
        :icon-component="Package"
        :active-names="['skills', 'agent', 'agentUsage', 'agentUsageHistory']"
        link-id="sidebar-skills-nav-link"
        :has-sub-nav="true"
        padding-class="px-2.5 py-1.5"
        :is-expanded="isExpanded" />
    </div>

    <div class="pt-px-8 mt-auto">
      <Divider spacing="sm" />
      <SidebarNavItem
        label="Tutorials"
        to="/tutorials"
        :icon-component="GraduationCap"
        :active-names="['tutorials']"
        link-id="sidebar-tutorials-nav-link"
        padding-class="px-2.5 py-1.5"
        :is-expanded="isExpanded" />

      <Divider spacing="sm" />

      <ProfileMenu
        :is-expanded="isExpanded"
        :l-c-ada-is-shown="lCAdaIsShown"
        @toggle-l-c-ada="(shown) => emit('toggleLCAda', shown)" />
    </div>
  </div>
</template>
